import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BsFillArrowUpCircleFill } from "react-icons/bs";
import Sidebar from "../components/Common/Sidebar";
import MobileNav from "../components/Common/MobileNav";
import SearchBox from "../components/Common/SearchBox";
import SidebarMini from "../components/Common/SidebarMini";
import PageTitle from "../components/Common/PageTitle";
import ExploreFilter from "../components/Explore/ExploreFilter";
import ExploreResult from "../components/Explore/ExploreResult";
import { useCurrentViewportView } from "../hooks/useCurrentViewportView";
import { getTodaysDate } from "../shared/utils";
import { getMoviesGenresList, getTVGenresList } from "../services/home";

const Explore = () => {
  const [currentTab, setCurrentTab] = useState(
    localStorage.getItem("currentTab") || "movie"
  );

  const [searchParams, setSearchParams] = useSearchParams();

  const { isMobile } = useCurrentViewportView();

  const [isShowScrollUpBtn, setIsShowScrollUpBtn] = useState(false);
  const [isSidebarActive, setIsSidebarActive] = useState(false);

  useEffect(() => {
    const checkIfShowScrollUpBtn = () => {
      const scrollOffset = document.documentElement.scrollTop;
      if (scrollOffset > 500) {
        setIsShowScrollUpBtn(true);
      } else {
        setIsShowScrollUpBtn(false);
      }
    };

    window.addEventListener("scroll", checkIfShowScrollUpBtn);

    return () => window.removeEventListener("scroll", checkIfShowScrollUpBtn);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const [config, setConfig] = useState({});

  useEffect(() => {
    const changeConfig = (key, value) => {
      setConfig((prevConfig) => ({
        ...prevConfig,
        [key]: value,
      }));
    };

    const sortType = searchParams.get("sort_by") || "popularity.desc";
    changeConfig("sort_by", sortType);

    const genreType = searchParams.getAll("genre") || [];
    changeConfig("with_genres", genreType.toString());

    const minRuntime = Number(searchParams.get("minRuntime")) || 0;
    const maxRuntime = Number(searchParams.get("maxRuntime")) || 200;
    changeConfig("with_runtime.gte", minRuntime);
    changeConfig("with_runtime.lte", maxRuntime);

    const releaseFrom = searchParams.get("from") || "2001-03-04";
    const releaseTo = searchParams.get("to") || getTodaysDate();
    changeConfig("primary_release_date.gte", releaseFrom);
    changeConfig("primary_release_date.lte", releaseTo);
    changeConfig("air_date.gte", releaseFrom);
    changeConfig("air_date.lte", releaseTo);

    // eslint-disable-next-line
  }, [location.search]);

  const {
    data: moviesGenres,
    isLoading: isLoadingMoviesGenres,
    isError: isErrorMoviesGenres,
    error: errorMoviesGenres,
  } = useQuery(["movies-genres"], getMoviesGenresList);

  const {
    data: tvGenres,
    isLoading: isLoadingTVGenres,
    isError: isErrorTVGenres,
    error: errorTVGenres,
  } = useQuery(["tv-genres"], getTVGenresList);

  // GENRES

  if (isErrorMoviesGenres) return <p>ERROR: ${errorMoviesGenres.message}</p>;

  if (isLoadingMoviesGenres) return <p>Loading...</p>;

  if (isErrorTVGenres) return <p>ERROR: ${errorTVGenres.message}</p>;

  if (isLoadingTVGenres) return <p>Loading...</p>;

  return (
    <>
      <PageTitle value={"Explore | SHWETFLIX"} />
      {isShowScrollUpBtn && (
        <button
          onClick={scrollToTop}
          className={`fixed bottom-[30px] right-[30px] z-10 transition duration-500 ${
            isShowScrollUpBtn ? "opacity-100" : "opacity-0"
          }`}
        >
          <BsFillArrowUpCircleFill
            size={45}
            className="text-gray-darken brightness-150 hover:brightness-200 transition duration-300"
          />
        </button>
      )}

      <MobileNav setIsSidebarActive={setIsSidebarActive} />
      <div className="flex flex-col md:flex-row">
        {!isMobile && <SidebarMini />}
        {isMobile && (
          <Sidebar
            setIsSidebarActive={setIsSidebarActive}
            isSidebarActive={isSidebarActive}
          />
        )}
        <div className="flex-grow px-4 pt-8">
          <div className="flex flex-col justify-between items-center mb-24 md:mb-8">
            <h2 className="text-white text-3xl font-medium uppercase">
              Find {currentTab === "movie" ? "Movie" : "TV Show"} that best fit
              you
            </h2>
            <div className="relative max-w-[350px] w-full md:-mt-24">
              <SearchBox />
            </div>
          </div>

          <div className="inline-flex gap-[10px] pb-[14px] relative">
            <button
              onClick={() => {
                setCurrentTab("movie");
                localStorage.setItem("currentTab", "movie");
                setSearchParams({});
              }}
              className={`px-3 py-2 w-[100px] rounded-full outline-0 border-0 mr-2 text-sm text-gray-400 
                  transition duration-300 hover:text-white
                 ${currentTab === "movie" && "!text-white tw-shaded-button"}`}
            >
              Movie
            </button>
            <button
              onClick={() => {
                setCurrentTab("tv");
                localStorage.setItem("currentTab", "tv");
                setSearchParams({});
              }}
              className={`px-4 py-2 w-[100px] rounded-full outline-0 border-0 mr-2 text-sm text-gray-400 
                transition duration-300 hover:text-white
                ${currentTab === "tv" && "!text-white tw-shaded-button"} `}
            >
              TV Show
            </button>
          </div>
          {isMobile && (
            <div className="shrink-0 max-w-[300px] w-full py-3">
              <ExploreFilter
                currentTab={currentTab}
                moviesGenres={moviesGenres}
                tvGenres={tvGenres}
              />
            </div>
          )}
          <ExploreResult
            currentTab={currentTab}
            config={config}
            moviesGenres={moviesGenres}
            tvGenres={tvGenres}
          />
        </div>
        {!isMobile && (
          <div className="shrink-0 max-w-[300px] w-full py-8 px-3">
            <ExploreFilter
              currentTab={currentTab}
              moviesGenres={moviesGenres}
              tvGenres={tvGenres}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default Explore;
